import React, { useState, useContext, createContext, useEffect } from "react";
import {
  Auth,
  CognitoUser, // eslint-disable-line no-unused-vars
} from "@aws-amplify/auth";
import * as Sentry from "@sentry/react";
import apiCDSApp from "../services/apiCDSApp";
import { secureStorage } from "../util/SecureStorage";
import { requests } from "../services/requests";
import Datalayer from "../services/datalayer";

/**
 * @typedef TAuthContext
 * @property {boolean} loading
 * @property {() => void} setIsLoggedIn
 * @property {boolean} isLoggedIn
 * @property {() => Promise<any>} signIn
 * @property {() => Promise<{status: boolean, message: string, userId: string}>} signUp
 * @property {() => Promise<any>} signOut
 * @property {() => Promise<any>} forgotPassword
 * @property {() => Promise<any>} recoverPassword
 * @property {(email: string, answer: string) => Promise<any>} answerCustomChallenge
 */

/** @type {import('react').Context<TAuthContext>} */
const AuthContext = createContext();
AuthContext.displayName = "Autenticação";

const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  async function loadAuthSession() {
    setLoading(true);

    try {
      const session = await Auth.currentSession();

      if (session.isValid()) {
        const jwtToken = session.getIdToken().getJwtToken();
        apiCDSApp.defaults.headers.common["accessToken"] = jwtToken;

        setIsLoggedIn(true);
      }
    } catch (error) {
      console.error({ error });
      setIsLoggedIn(false);
      // TODO: verificar se deve manter esse log
      if (error && !error.includes("No current user")) {
        const sentryError = new Error(error.message);
        Sentry.captureException(sentryError, (scope) => {
          scope.setTransactionName(`Auth Context: ${error.code}`);
          scope.setTag("loadAuthSession", error.code);
          scope.setExtra("data", error);
          return scope;
        });
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Every time the App is opened, this provider is rendered
    // and call de loadAuthSession function.
    loadAuthSession();
    // it also clear localStorage reference id
    // used for datalayer.
    secureStorage.removeItem("reference");
  }, []);

  /**
   * signInSuccess
   * @param {string} email
   */
  async function signInSuccess(email) {
    try {
      const session = await Auth.currentSession();
      const jwtToken = session.getIdToken().getJwtToken();
      apiCDSApp.defaults.headers.common["accessToken"] = jwtToken;
      requests
        .getUser()
        .then(({ data }) => {
          const userId = data && data.userData && data.userData.id;
          Datalayer.onLoginCallback("sucesso", userId);
        })
        .catch((error) => console.error(error));
    } catch (error) {
      signInError(email, error, "signInSuccess");
    }
  }

  /**
   * signInError
   * @param {string} email
   * @param {Error} error
   * @param {string} flow
   */
  function signInError(email, error, flow) {
    Datalayer.onLoginCallback("erro", undefined);
    const sentryError = new Error(error.message);
    Sentry.captureException(sentryError, (scope) => {
      scope.setTransactionName(`Auth Context[${flow}]: ${error.code}`);
      scope.setUser({ email });
      scope.setTag("signIn", error.code);
      scope.setExtra("data", error);
      return scope;
    });
    throw error;
  }

  /**
   * SignIn
   * @param {string} email
   * @param {string} password
   * @returns {Promise<CognitoUser | any>}
   */
  async function signIn(email, password) {
    try {
      const authResponse = await Auth.signIn(email, password);

      if (!authResponse.signInUserSession) return authResponse;

      await signInSuccess(email);
    } catch (error) {
      signInError(email, error, "password");
    }
  }

  /**
   * answerCustomChallenge
   * @param {string} email
   * @param {string} answer
   * @returns {Promise<any>}
   */
  async function answerCustomChallenge(email, answer) {
    try {
      let cognitoUser = await Auth.signIn(email);
      const authResponse = await Auth.sendCustomChallengeAnswer(
        cognitoUser,
        answer
      );

      if (!authResponse.signInUserSession) return authResponse;

      await signInSuccess(email);
    } catch (error) {
      signInError(email, error, "magicLink");
    }
  }

  /**
   * SignUp
   * @param {string} name
   * @param {string} email
   * @param {string} password
   * @param {string} phoneNumber
   * @returns {Promise<{status: boolean, message: string, userId: string}>}
   */
  async function signUp(name, email, password, phoneNumber) {
    try {
      const { message, userId } = await requests.signup(
        name,
        email,
        password,
        phoneNumber
      );

      await Auth.signIn(email, password);

      const session = await Auth.currentSession();
      const jwtToken = session.getIdToken().getJwtToken();
      apiCDSApp.defaults.headers.common["accessToken"] = jwtToken;

      return { message, userId };
    } catch (error) {
      const sentryError = new Error(error.message);
      Sentry.captureException(sentryError, (scope) => {
        scope.setTransactionName(`Auth Context: ${error.code}`);
        scope.setUser({ email });
        scope.setTag("signUp", error.code);
        scope.setExtra("data", error);
        return scope;
      });
      throw error;
    }
  }

  async function signOut() {
    await Auth.signOut();
    delete apiCDSApp.defaults.headers.common["accessToken"];
    if (window) localStorage.clear();
    setIsLoggedIn(false);
  }

  async function forgotPassword(email) {
    return Auth.forgotPassword(email);
  }

  async function recoverPassword(email, code, new_password) {
    return Auth.forgotPasswordSubmit(email, code, new_password);
  }

  return (
    <AuthContext.Provider
      value={{
        loading,
        setIsLoggedIn,
        isLoggedIn,
        signIn,
        signUp,
        signOut,
        forgotPassword,
        recoverPassword,
        answerCustomChallenge,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 *
 * @returns AuthContext
 */
const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return context;
};

export { AuthContext, AuthProvider, useAuthContext };
